import { NetworkStatus } from '@apollo/client';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Card,
	DeleteIcon,
	DocumentIcon,
	DownloadIcon,
	Empty,
	FlexGap,
	Heading,
	IconButton,
	ListItem,
	Spinner,
	UploadIcon,
	WarningIcon,
	tokens,
} from '@treams/treams-ui';

function stopRowClick(event: MouseEvent) {
	event.stopPropagation();
}

function UserDocumentEntry(props: { document: UserDocument; onDocumentDeleted: () => void }) {
	const { t } = useTranslation();
	const [deleting, setDeleting] = useState(false);

	const [createDownloadURL, { loading: downloadURLLoading }] =
		graphql.useCreateUserDocumentDownloadUrlMutation({
			variables: {
				input: {
					documentID: props.document.id,
				},
			},
		});

	const [deleteDocument] = graphql.useDeleteUserDocumentMutation({
		variables: {
			input: {
				documentID: props.document.id,
			},
		},
	});

	const isDownloadable = props.document.status === 'PROCESSING_OK';
	const hasError =
		props.document.status !== 'PROCESSING' && props.document.status !== 'PROCESSING_OK';

	const onClickDownload = async () => {
		if (!isDownloadable) {
			return;
		}

		const result = await createDownloadURL();
		const url = result.data?.createUserDocumentV2DownloadURL.url;
		if (url) {
			window.open(url, '_blank');
		}
	};

	const onClickDelete = async () => {
		try {
			const shouldDelete = window.confirm(t('user.documents.delete.confirm.title'));
			if (shouldDelete) {
				setDeleting(true);
				await deleteDocument();
				props.onDocumentDeleted();
			}
		} catch (error) {
			window.alert(t('user.documents.delete.error'));
			setDeleting(false);
			props.onDocumentDeleted();
		}
	};

	const busy = deleting || downloadURLLoading || props.document.status === 'PROCESSING';

	return (
		<ListItem
			icon={hasError ? <WarningIcon /> : <DocumentIcon />}
			title={props.document.name}
			meta={`${props.document.createdBy.firstName} ${props.document.createdBy.lastName}`}
			metaAccent={hasError ? t('user.documents.file-processing-error') : undefined}
			onClick={isDownloadable ? onClickDownload : undefined}
			trailing={
				<FlexGap alignItems="center" gap={tokens.gap.element} onClick={stopRowClick}>
					{busy ? (
						<Spinner />
					) : (
						<>
							{isDownloadable && (
								<IconButton icon={<DownloadIcon />} type="secondary" onClick={onClickDownload} />
							)}
							{props.document.viewerCanDelete && (
								<IconButton icon={<DeleteIcon />} type="destructive" onClick={onClickDelete} />
							)}
						</>
					)}
				</FlexGap>
			}
		/>
	);
}

function UserDocumentUpload(props: { userID: string; onUploadFinished: () => void }) {
	const { t } = useTranslation();
	const ref = useRef<HTMLInputElement>(null);

	const [create] = graphql.useCreateUserDocumentMutation();
	const [finalize] = graphql.useFinalizeUserDocumentUploadMutation();
	const [loading, setLoading] = useState(false);

	const upload = async (file: File) => {
		try {
			setLoading(true);
			const createResult = await create({
				variables: {
					input: {
						userID: props.userID,
						name: file.name,
						contentLength: file.size,
					},
				},
			});

			const documentID = createResult.data?.createUserDocumentV2.documentID!;
			const uploadURL = createResult.data?.createUserDocumentV2.uploadUrl!;
			const uploadHeaders = createResult.data?.createUserDocumentV2.uploadHeaders!;
			const headers = new Headers();

			for (const { name, values } of uploadHeaders) {
				for (const value of values) {
					headers.append(name, value);
				}
			}

			const uploadResult = await fetch(uploadURL, {
				method: 'PUT',
				headers,
				body: file,
			});

			if (uploadResult.ok) {
				setLoading(false);
				await finalize({ variables: { input: { documentID: documentID } } });
				props.onUploadFinished();
			} else {
				// noinspection ExceptionCaughtLocallyJS
				throw new Error(uploadResult.statusText);
			}
		} catch (error) {
			setLoading(false);
			alert(t('user.documents.uploadButton.error'));
		}
	};

	async function onChange() {
		if (!ref.current) {
			return;
		}

		const file = ref.current.files?.[0];

		if (!file) {
			return;
		}

		await upload(file);
	}

	return (
		<>
			<input ref={ref} type="file" style={{ display: 'none' }} onChange={onChange} />
			<Button
				onClick={() => ref.current?.click()}
				loading={loading}
				title={t('user.documents.uploadButton.title')}
				icon={<UploadIcon />}
				small
				type="secondary"
			/>
		</>
	);
}

export default function UserDocuments(props: { user: graphql.User }) {
	const MAX_POLL_ITERATIONS = 100;

	const { t } = useTranslation();
	const [polling, setPolling] = useState(false);
	const [_, setPollIterationCount] = useState(0);
	const { data, loading, previousData, refetch, stopPolling, networkStatus } =
		graphql.useUserDocumentsQuery({
			variables: { id: props.user.id },
			pollInterval: polling ? 1000 : 0,
			notifyOnNetworkStatusChange: true,
		});
	const documents = data && [...(data.user.documentsV2 || [])];

	const onListUpdated = async () => {
		await refetch();
	};

	// We cannot do a useEffect on the useQuery's `data`, as that stays the same between polls if there was no difference to the previous data.
	useEffect(() => {
		if (networkStatus !== NetworkStatus.ready || loading) {
			return;
		}

		const hasProcessingDocuments = documents?.find((d) => d.status === 'PROCESSING');
		if (hasProcessingDocuments) {
			setPollIterationCount((prev) => {
				const newCount = prev + 1;

				if (newCount >= MAX_POLL_ITERATIONS) {
					setPolling(false);
					stopPolling?.();
				} else {
					setPolling(true);
				}

				return newCount;
			});
		} else {
			setPolling(false);
		}
	}, [setPolling, setPollIterationCount, networkStatus, stopPolling]);

	const showLoadingIndicator = loading && !polling && !previousData;
	const isEmpty = !showLoadingIndicator && (!documents || documents.length === 0);

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.group}>
			<FlexGap alignItems="center" justifyContent="space-between">
				<Heading>{t('user.documents.title')}</Heading>
				{props.user.viewerCanCreateDocuments && (
					<UserDocumentUpload userID={props.user.id} onUploadFinished={onListUpdated} />
				)}
			</FlexGap>
			{showLoadingIndicator && <Spinner centered />}
			{!showLoadingIndicator && documents && documents.length > 0 && (
				<Card p={0}>
					{documents.map((doc) => (
						<UserDocumentEntry key={doc.id} document={doc} onDocumentDeleted={onListUpdated} />
					))}
				</Card>
			)}
			{isEmpty && <Empty.Card icon={<DocumentIcon />} title={t('user.documents.empty')} />}
		</FlexGap>
	);
}
